import type { CastProfile } from './castProfiles.ts'
import type { CastProfileTimeline, CastSeasonChapter } from './castProfileTimelines.ts'

// Original source-backed editorial copy, reviewed October 1, 2026.
// Chapters describe credited-season story context, not appearances in every episode.
// Family and friend introductions stay nonromantic; historical milestones do not
// certify present relationship statuses. Research provenance lives in the canon manifest.
export const darceyStaceyCanonProfiles: CastProfile[] = [
  {
    "personId": "p-georgi-rusev",
    "headline": "A Bulgarian romance arrived by DM, then discovered the Silva family has excellent follow-up questions.",
    "bio": [
      "Georgi was introduced as a Bulgarian massage therapist living near Washington, D.C. Darcey said they connected on social media and first met in Miami during the Super Bowl in February 2020. Their Darcey & Stacey introduction included a flirty FaceTime bath call; this courtship did not begin with a shortage of bubbles.",
      "Quarantine quickly brought them together, followed by a Rhode Island proposal and recurring disagreements about money, trust and his previous marriage. Counseling and another engagement kept the story moving before Georgi returned during Stacey and Florian’s wedding preparations. TLC later documented Darcey and Georgi’s November 11, 2023 wedding, a dated milestone beyond the four indexed seasons."
    ],
    "originStory": "After messaging on social media, Georgi and Darcey met in Miami during the February 2020 Super Bowl weekend. Darcey & Stacey Season 1 introduced their relationship as it grew from new dates into quarantine together and an engagement.",
    "journeys": [
      {
        "label": "The journey: Georgi & Darcey",
        "partnerName": "Darcey Silva",
        "relationshipId": "ARC-DS-DARCEY-GEORGI",
        "era": "Darcey & Stacey S1–S4",
        "summary": "Their relationship ran through a rapid engagement, trust disputes, a breakup and attempts to reconnect. TLC documented a November 2023 wedding after the four indexed seasons; present status remains a separate review."
      }
    ],
    "sources": [
      {
        "label": "People: Georgi’s introduction and how they met",
        "url": "https://people.com/tv/90-day-fiance-darcey-facetimes-her-boyfriend-georgi-while-naked-in-a-bathtub/"
      },
      {
        "label": "TLC: the first engagement",
        "url": "https://www.tlc.com/shows/90-day-fiance/articles/darcey-and-georgi-are-engaged"
      },
      {
        "label": "Prime Video / TLC: season 2",
        "url": "https://www.primevideo.com/-/de/detail/0QW4NOTJRIFXU35QS17H1QIV9Z?tr=um"
      },
      {
        "label": "Prime Video / TLC: season 3",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      },
      {
        "label": "TLC: season 4 episodes",
        "url": "https://www.tlc.com/shows/darcey-and-stacey"
      },
      {
        "label": "TLC: November 2023 wedding",
        "url": "https://www.tlc.com/shows/darcey-and-stacey/photo-galleries/darcey-georgi-wedding-day-photos"
      }
    ]
  },
  {
    "personId": "p-mike-silva",
    "headline": "The Silva family’s father, with a calculator for business plans and a few questions for prospective sons-in-law.",
    "bio": [
      "Mike Silva is Darcey and Stacey’s father and Aniko and Aspen’s grandfather. The twins’ direct launch interview introduced him and their mother, Nancy, as long-divorced parents who remained friends. Darcey & Stacey gave viewers a closer look at the family patriarch the sisters said they had always looked up to.",
      "His support came with practical questions: season 2 brought conversations about Georgi’s finances, and season 3 put both his investment in House of Eleven and objections to a proposed twin wedding on screen. By season 4, concern for his health brought the twins back to Connecticut. In this family, a quiet meeting rarely stays a quiet meeting."
    ],
    "originStory": "Mike entered Darcey & Stacey as the twins’ father when the family opened its doors for the first season. The launch interview described this as his official reality television debut alongside Nancy; his connection is parental.",
    "journeys": [
      {
        "label": "The family chapter: the Silva twins",
        "era": "Darcey & Stacey S1–S4",
        "summary": "Mike’s family role connects the twins, their children, business plans and relationship decisions. Financial questions and the proposed twin wedding gave his supporting story its own stakes."
      }
    ],
    "sources": [
      {
        "label": "Entertainment Weekly: direct family launch interview",
        "url": "https://ew.com/tv/90-day-fiance-stars-darcey-stacey-silva-spin-off-series/"
      },
      {
        "label": "Showbiz Cheat Sheet: Mike and Nancy’s season 2 visit",
        "url": "https://www.cheatsheet.com/news/darcey-stacey-stacey-silva-talks-parents-concerns-georgi-rusev.html/"
      },
      {
        "label": "Prime Video / TLC: the season 3 business story",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      },
      {
        "label": "People: Mike’s response to the proposed twin wedding",
        "url": "https://people.com/tv/darcey-and-stacey-father-mike-says-she-shouldnt-marry-georgi-wont-attend-wedding/"
      },
      {
        "label": "TLC: the season 4 family health chapter",
        "url": "https://www.tlc.com/shows/darcey-and-stacey"
      }
    ]
  },
  {
    "personId": "p-nancy-silva",
    "headline": "Nana to the family, mother to the twins, and a familiar face at the Silva family table.",
    "bio": [
      "Nancy Silva is Darcey and Stacey’s mother and the grandmother the family calls Nana. In the sisters’ direct interview announcing their spinoff, they introduced Nancy alongside Mike, explaining that the former spouses had remained friends after their divorce. Stacey described her mother as loving and helpful, giving Nana an introduction from someone who knew her well.",
      "The series brought that family role onto the screen. Nancy and Mike visited Stacey’s new apartment in season 2 and heard her concerns about Georgi and Darcey’s finances. Season 3 brought Nancy to Debbie and Reina’s family party ahead of the twins’ Miami fashion trip. Around the Silva twins, even an apartment visit can come with a full agenda."
    ],
    "originStory": "The twins introduced Nancy as their mother when Darcey & Stacey began. The family launch interview identified this as her official reality television debut; Nana’s route into the story was family life with her daughters.",
    "journeys": [
      {
        "label": "The family chapter: Nancy and her daughters",
        "era": "Darcey & Stacey S1–S4",
        "summary": "Nancy appears as a mother, grandmother and co-parent within the Silva family. Her past marriage to Mike supplies family context, not an invented active romance."
      }
    ],
    "sources": [
      {
        "label": "Entertainment Weekly: the twins introduce Nana",
        "url": "https://ew.com/tv/90-day-fiance-stars-darcey-stacey-silva-spin-off-series/"
      },
      {
        "label": "Showbiz Cheat Sheet: Nancy and Mike visit Stacey",
        "url": "https://www.cheatsheet.com/news/darcey-stacey-stacey-silva-talks-parents-concerns-georgi-rusev.html/"
      },
      {
        "label": "Soap Dirt: Nancy at the family’s season 3 party",
        "url": "https://soapdirt.com/darcey-stacey-recap-georgi-rusev-hoses-the-twins-season-3-episode-3-crowned-and-miami-bound/"
      },
      {
        "label": "IMDb: season 3 family cast metadata",
        "url": "https://pro.imdb.com/title/tt17005862/"
      },
      {
        "label": "IMDb: season 4 family cast metadata",
        "url": "https://pro.imdb.com/title/tt26772066/"
      }
    ]
  },
  {
    "personId": "p-aniko-bollok",
    "headline": "Darcey’s eldest daughter, with a pageant sash, a college move and occasional questions for the adults.",
    "bio": [
      "Aniko Bollok is Darcey’s elder daughter and Aspen’s sister. She was already familiar through her mother’s earlier franchise story when Darcey & Stacey opened up the family’s daily life. Her own milestones became part of the spinoff: a Sweet Sixteen in season 2, a Miss Teen Connecticut pageant in season 3 and a college move to Arizona in season 4.",
      "She and Aspen also asked Georgi about his intentions with their mother and discussed the attention that comes with televised family life at the first Tell All. Aniko’s story includes her bond with her sister and the transition away from home; the family group chat apparently had no plans to graduate alongside her."
    ],
    "originStory": "Aniko entered the story through her mother, Darcey, and became part of the family-centered Darcey & Stacey cast from its first indexed season. Her earlier Before the 90 Days exposure is acknowledged without assigning an unverified exact debut season.",
    "journeys": [
      {
        "label": "The family chapter: growing up in Darcey’s story",
        "era": "Darcey & Stacey S1–S4",
        "summary": "The series follows Aniko’s family role, Sweet Sixteen, pageant experience and move to college. Her connection to Darcey and Aspen is familial; no romantic debut is assigned."
      }
    ],
    "sources": [
      {
        "label": "Entertainment Weekly: the launch family interview",
        "url": "https://ew.com/tv/90-day-fiance-stars-darcey-stacey-silva-spin-off-series/"
      },
      {
        "label": "Prime Video / TLC: Aniko’s Sweet Sixteen",
        "url": "https://www.primevideo.com/-/de/detail/0QW4NOTJRIFXU35QS17H1QIV9Z?tr=um"
      },
      {
        "label": "Apple TV / TLC: the sisters’ season 2 family story",
        "url": "https://tv.apple.com/ae/show/darcey-stacey/umc.cmc.1yupks2dtqw6ufzwk4c38yiwe"
      },
      {
        "label": "Prime Video / TLC: the pageant and Tell All",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      },
      {
        "label": "TLC: Aniko leaves for college in season 4",
        "url": "https://www.tlc.com/shows/darcey-and-stacey"
      }
    ]
  },
  {
    "personId": "p-aspen-bollok",
    "headline": "Darcey’s younger daughter, sharing a sisterly bond and a front-row seat to the family’s big decisions.",
    "bio": [
      "Aspen Bollok is Darcey’s younger daughter and Aniko’s sister. The spinoff introduced both daughters as part of the Silva family, giving them room within their mother and aunt’s bigger story. In season 2, licensed episode notes follow Mike taking the sisters to look at cars; their family scenes extended beyond the adults’ latest relationship meeting.",
      "Season 3 brought Aspen and Aniko’s direct questions for Georgi about Darcey, followed by a Tell All discussion of social media attention. In season 4, Aniko’s college departure marked a new family chapter. Aspen’s perspective provides a useful reminder that a mother’s love story can become her children’s very eventful household timetable."
    ],
    "originStory": "Aspen’s link to the franchise is her mother, Darcey. She appears in Darcey & Stacey’s first indexed family season after earlier exposure in Darcey’s story; an exact first-ever Before the 90 Days season is not claimed here.",
    "journeys": [
      {
        "label": "The family chapter: Aspen, Aniko and Darcey",
        "era": "Darcey & Stacey S1–S4",
        "summary": "Aspen’s story follows her family bonds, questions about Georgi and the changes around Aniko’s departure for college. A family introduction is not treated as a dating arc."
      }
    ],
    "sources": [
      {
        "label": "Entertainment Weekly: Darcey’s daughters at the series launch",
        "url": "https://ew.com/tv/90-day-fiance-stars-darcey-stacey-silva-spin-off-series/"
      },
      {
        "label": "Apple TV / TLC: Mike and the daughters look at cars",
        "url": "https://tv.apple.com/ae/show/darcey-stacey/umc.cmc.1yupks2dtqw6ufzwk4c38yiwe"
      },
      {
        "label": "Prime Video / TLC: the sisters question Georgi and join the Tell All",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      },
      {
        "label": "TLC: season 4’s college departure",
        "url": "https://www.tlc.com/shows/darcey-and-stacey"
      },
      {
        "label": "IMDb: Aspen’s season 4 cast credit",
        "url": "https://pro.imdb.com/title/tt26772066/"
      }
    ]
  },
  {
    "personId": "p-michael-benz",
    "headline": "A fashion collaborator with blue hair, strong opinions and no intention of blending into the background.",
    "bio": [
      "Michael Benz is the Silva twins’ longtime friend and fashion collaborator. They explained on Darcey & Stacey that he helped with their early House of Eleven work in Los Angeles, including swimwear samples. His larger season 3 introduction brought him into the Miami Swim Week story, where professional support and personal advice shared the same itinerary.",
      "A 2023 profile described his clothing design work and distinctive individual pieces. On the show, Michael also spoke openly about the twins’ relationships, putting him at odds with Florian and Georgi as season 4’s wedding events approached. His blue hair was memorable; the conversations were not exactly muted either."
    ],
    "originStory": "Michael entered Darcey & Stacey through his established friendship and fashion work with Darcey and Stacey. Season 3 brought that collaboration into the Miami fashion-show chapter; he was introduced as a friend and business collaborator.",
    "journeys": [
      {
        "label": "The collaboration: Michael and the Silva twins",
        "era": "Darcey & Stacey S3–S4",
        "summary": "A friendship from the twins’ Los Angeles fashion beginnings became a recurring Miami business and family story. Advice about their partners brought additional tension around the wedding."
      }
    ],
    "sources": [
      {
        "label": "Distractify: Michael’s introduction and fashion collaboration",
        "url": "https://www.distractify.com/p/michael-benz-darcey-and-stacey"
      },
      {
        "label": "Prime Video / TLC: Michael and Miami Swim Week",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      },
      {
        "label": "TLC: season 4’s launch and wedding tensions",
        "url": "https://www.tlc.com/shows/darcey-and-stacey"
      },
      {
        "label": "TV Guide: Michael Benz’s cast identity",
        "url": "https://www.tvguide.com/tvshows/darcey-and-stacey/cast/1030816996/"
      }
    ]
  },
  {
    "personId": "p-debbie-darcey-stacey",
    "headline": "A longtime friend whose catch-up dinners tend to arrive with questions already prepared.",
    "bio": [
      "Debbie is one of Darcey and Stacey’s longtime friends, seen alongside Reina in the twins’ supporting circle. A licensed first-season preview shows the friends asking Florian about photographs with another woman; another dinner introduced Georgi to them. Debbie’s part in the story was friendship with a side order of direct questions.",
      "During season 2, she relayed disputed comments attributed to Georgi, contributing to the couple’s trust troubles. Those accusations remain part of the televised disagreement rather than verified statements of fact. Season 3’s episode notes name Debbie and Reina’s party, keeping the friends in the family’s orbit. This profile uses her documented first name and keeps her distinct from the franchise’s other Debbies."
    ],
    "originStory": "Debbie entered Darcey & Stacey as an established friend of the twins, appearing with Reina in the first season’s dinner conversations. Her introduction was a supporting friendship.",
    "journeys": [
      {
        "label": "The friendship: Debbie and the Silva twins",
        "era": "Darcey & Stacey S1–S3",
        "summary": "Debbie joined relationship discussions, introduced-person dinners and the party storyline with Reina. The catalog certifies these credited seasons, not every individual episode appearance."
      }
    ],
    "sources": [
      {
        "label": "Entertainment Tonight: the friends’ licensed season 1 preview",
        "url": "https://www.etonline.com/darcey-stacey-florian-freaks-out-after-hes-confronted-about-pics-with-another-woman-exclusive"
      },
      {
        "label": "TV Insider: meeting Georgi in season 1",
        "url": "https://www.tvinsider.com/952185/darcey-stacey-recap-season-1-episode-9/"
      },
      {
        "label": "Prime Video / TLC: the disputed season 2 gossip",
        "url": "https://www.primevideo.com/-/de/detail/0QW4NOTJRIFXU35QS17H1QIV9Z?tr=um"
      },
      {
        "label": "Prime Video / TLC: Debbie and Reina’s season 3 party",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      }
    ]
  },
  {
    "personId": "p-reina-darcey-stacey",
    "headline": "A Silva family friend with an opinion ready before the dinner menu arrives.",
    "bio": [
      "Reina is a longtime friend of Darcey and Stacey, frequently appearing alongside Debbie. The first-season story brought her into questions about Florian’s photographs and a dinner meeting with Georgi. She gave Darcey her impression of the new relationship while asking about the previous marriage he had not yet finished.",
      "She also took part in season 2’s disagreement over secondhand claims about Georgi, which are presented as disputed gossip rather than proven conduct. The following season’s licensed episode notes identify a party hosted by Reina and Debbie. Her supporting story is friendship with the twins, and the documented first name is enough to keep that connection clear without inventing a surname."
    ],
    "originStory": "Reina joined Darcey & Stacey through her longstanding friendship with the twins, alongside Debbie. First-season licensed footage identifies that supporting connection; no romance is assigned to her entrance.",
    "journeys": [
      {
        "label": "The friendship: Reina and the Silva twins",
        "era": "Darcey & Stacey S1–S3",
        "summary": "Reina appears in the twins’ discussions about trust, Georgi’s introduction and a later party with Debbie. Her credit is tied to researched seasons rather than an assumed episode-by-episode presence."
      }
    ],
    "sources": [
      {
        "label": "Entertainment Tonight: Reina in the licensed first-season clip",
        "url": "https://www.etonline.com/darcey-stacey-florian-freaks-out-after-hes-confronted-about-pics-with-another-woman-exclusive"
      },
      {
        "label": "TV Insider: Reina’s meeting with Georgi",
        "url": "https://www.tvinsider.com/952185/darcey-stacey-recap-season-1-episode-9/"
      },
      {
        "label": "Prime Video / TLC: Reina in season 2’s trust story",
        "url": "https://www.primevideo.com/-/de/detail/0QW4NOTJRIFXU35QS17H1QIV9Z?tr=um"
      },
      {
        "label": "Prime Video / TLC: Reina and Debbie’s season 3 party",
        "url": "https://www.primevideo.com/-/de/detail/0I6O3OT1J0KPVB4WRWXHUJGCG3"
      }
    ]
  }
]

export const darceyStaceyCanonTimelines: Record<string, CastProfileTimeline> = {
  "p-georgi-rusev": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "First seen as Darcey’s new love interest",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "romantic"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "His online connection became an engagement, repeated attempts to repair trust and a wedding documented by TLC in November 2023.",
        "relationshipId": "ARC-DS-DARCEY-GEORGI"
      },
      {
        "name": "Florian Sukaj",
        "personId": "p-florian-sukaj",
        "context": "Stacey’s husband and the friend whose season 4 wedding-party plans brought Georgi back into Darcey’s orbit."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "Darcey’s twin and an outspoken participant in the family’s questions about their relationship."
      },
      {
        "name": "Michael Benz",
        "personId": "p-michael-benz",
        "context": "The twins’ friend and fashion collaborator, with whom tensions escalated during season 4’s wedding events."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "From Miami meeting to a Rhode Island proposal",
        "recap": "Social-media messages led to a first meeting in Miami, then quarantine together as Darcey introduced Georgi to the family. Questions about his unfinished previous marriage accompanied an engagement at sea.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva"
        ],
        "relationshipId": "ARC-DS-DARCEY-GEORGI"
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "An engagement with trust on the agenda",
        "recap": "Money questions, a meeting with Georgi’s ex-wife and his conversation with Darcey’s ex Jesse put the engagement under pressure. The couple’s disagreements were still very much present when the twins headed to Turkey.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva"
        ],
        "relationshipId": "ARC-DS-DARCEY-GEORGI"
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Counseling, Miami and another wedding plan",
        "recap": "Georgi and Darcey tried counseling while Aniko’s pageant and the Miami fashion show put him alongside the family. Another engagement and a proposed twin wedding led to renewed questions about trust at the first two-part Tell All.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva"
        ],
        "relationshipId": "ARC-DS-DARCEY-GEORGI"
      },
      {
        "show": "Darcey & Stacey",
        "season": 4,
        "title": "Back in the wedding party’s orbit",
        "recap": "Florian’s wedding-party plans brought Georgi back while Darcey tried new dates. Tension with Michael Benz and a rehearsal-dinner revelation complicated an already crowded celebration.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-florian-sukaj",
          "p-michael-benz"
        ],
        "relationshipId": "ARC-DS-DARCEY-GEORGI"
      }
    ]
  },
  "p-mike-silva": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "Introduced as Darcey and Stacey’s father",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "family"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "His daughter, whose relationship decisions and fashion ambitions bring him into the family story."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "His daughter and Darcey’s twin; both sisters described looking up to their father."
      },
      {
        "name": "Nancy Silva",
        "personId": "p-nancy-silva",
        "context": "The twins’ mother and his former wife; the launch interview described them as friends after divorce."
      },
      {
        "name": "Aniko Bollok",
        "personId": "p-aniko-bollok",
        "context": "His granddaughter, whose teenage milestones and college move became part of the series."
      },
      {
        "name": "Aspen Bollok",
        "personId": "p-aspen-bollok",
        "context": "His granddaughter and Aniko’s sister, seen with him in the family’s supporting story."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "The twins introduce their father",
        "recap": "The family’s launch interview introduced Mike as Darcey and Stacey’s father, making his official reality-TV debut alongside Nancy. The new series brought their parents into a story previously centered on the twins’ romances.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva",
          "p-nancy-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "Questions at the family table",
        "recap": "Mike and Nancy visited Stacey’s new apartment as she raised concerns about Darcey and Georgi’s finances. Another family outing followed Mike taking Aniko and Aspen to look at cars.",
        "status": "aired",
        "partnerPersonIds": [
          "p-stacey-silva",
          "p-nancy-silva",
          "p-aniko-bollok",
          "p-aspen-bollok"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Fashion investment and wedding objections",
        "recap": "The twins’ Miami fashion-show plans brought questions about Mike’s investment in House of Eleven. He also objected to Darcey and Georgi’s renewed engagement and the proposed twin wedding, giving the celebration plans a very firm parental response.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 4,
        "title": "Family first during a health scare",
        "recap": "Concern for Mike’s health interrupted the twins’ Miami and wedding plans and brought them back to Connecticut. His family role became especially important as Stacey and Florian prepared for their ceremony.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva"
        ]
      }
    ]
  },
  "p-nancy-silva": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "Introduced as Darcey and Stacey’s mother, Nana",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "family"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "Her daughter and one of the twins who introduced Nancy to viewers."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "Her daughter, who described Nancy as a loving and helpful mother."
      },
      {
        "name": "Mike Silva",
        "personId": "p-mike-silva",
        "context": "The twins’ father and her former husband; their continued friendship supplies family context."
      },
      {
        "name": "Aniko Bollok",
        "personId": "p-aniko-bollok",
        "context": "Darcey’s elder daughter and Nancy’s granddaughter."
      },
      {
        "name": "Aspen Bollok",
        "personId": "p-aspen-bollok",
        "context": "Darcey’s younger daughter and Nancy’s granddaughter."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "Meet Nana",
        "recap": "The twins introduced their mother as Nana in the direct series-launch interview. Nancy and Mike’s official reality-TV debut widened the story to include the parents and grandparents behind the Silva family.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva",
          "p-mike-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "An apartment visit with a full agenda",
        "recap": "Nancy and Mike visited Stacey’s new apartment, where conversation turned to Darcey’s relationship with Georgi and Stacey’s worries about their finances. It was a family discussion, not a separate romance for Nancy.",
        "status": "aired",
        "partnerPersonIds": [
          "p-stacey-silva",
          "p-mike-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Family at the pre-Miami party",
        "recap": "Nancy joined Stacey at Debbie and Reina’s family party before the Miami fashion trip. The gathering put the parents, twins, friends and Georgi together while Darcey worked on repairing trust.",
        "status": "aired",
        "partnerPersonIds": [
          "p-stacey-silva",
          "p-darcey-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 4,
        "title": "A supporting family chapter",
        "recap": "Nancy remains a credited member of the extended Silva family in this season, which centers Aniko’s milestones and Stacey’s wedding preparations. The available sources establish her supporting family role without a separately documented personal storyline.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva",
          "p-aniko-bollok"
        ]
      }
    ]
  },
  "p-aniko-bollok": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "Darcey’s elder daughter in the first indexed family season",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "family"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "Her mother, whose franchise story first introduced the family."
      },
      {
        "name": "Aspen Bollok",
        "personId": "p-aspen-bollok",
        "context": "Her younger sister and companion through family milestones and questions for Georgi."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "Her aunt and Darcey’s twin, part of the family’s pageant and wedding chapters."
      },
      {
        "name": "Mike Silva",
        "personId": "p-mike-silva",
        "context": "Her grandfather, seen supporting the family and taking the sisters to look at cars."
      },
      {
        "name": "Nancy Silva",
        "personId": "p-nancy-silva",
        "context": "Her grandmother, known to the family as Nana."
      },
      {
        "name": "Georgi Rusev",
        "personId": "p-georgi-rusev",
        "context": "Her mother’s partner, whom Aniko and Aspen questioned about his intentions."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "Darcey’s story opens into family life",
        "recap": "Aniko joined the family-centered spinoff as Darcey’s elder daughter, alongside Aspen and the twins’ parents. This is her first indexed Darcey & Stacey season, not a claim that her earlier Before the 90 Days supporting appearances began here.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-aspen-bollok"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "A Sweet Sixteen of her own",
        "recap": "Aniko’s Sweet Sixteen became a family milestone while the adults worked through engagement and fertility questions. Licensed episode notes also followed Mike taking Aniko and Aspen to look at cars.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-aspen-bollok",
          "p-mike-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Pageant confidence and direct questions",
        "recap": "The Miss Teen Connecticut pageant gave Aniko a storyline of her own, with Darcey and the family supporting her. She and Aspen also questioned Georgi about his intentions and discussed the attention surrounding their family at the Tell All.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-aspen-bollok",
          "p-georgi-rusev"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 4,
        "title": "Prom, college and a family wedding",
        "recap": "Teenage milestones led into Aniko’s departure for college in Arizona. The move added a mother-daughter transition to a season already busy with House of Eleven plans and Stacey and Florian’s wedding.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-aspen-bollok",
          "p-stacey-silva"
        ]
      }
    ]
  },
  "p-aspen-bollok": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "Darcey’s younger daughter in the first indexed family season",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "family"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "Her mother, whose earlier franchise story introduced the daughters."
      },
      {
        "name": "Aniko Bollok",
        "personId": "p-aniko-bollok",
        "context": "Her elder sister, sharing family scenes and questions about their mother’s relationship."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "Her aunt and Darcey’s twin, part of the larger family story."
      },
      {
        "name": "Mike Silva",
        "personId": "p-mike-silva",
        "context": "Her grandfather, whose car-shopping outing with the sisters appears in licensed season 2 notes."
      },
      {
        "name": "Nancy Silva",
        "personId": "p-nancy-silva",
        "context": "Her grandmother, introduced as Nana."
      },
      {
        "name": "Georgi Rusev",
        "personId": "p-georgi-rusev",
        "context": "Her mother’s partner, whose intentions she and Aniko asked about in season 3."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "The family behind Darcey’s romance",
        "recap": "Aspen joined her mother, sister and extended family in the spinoff’s first indexed season. Her connection is familial, and her earlier supporting exposure in Darcey’s franchise story is acknowledged without inventing an exact original debut season.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-aniko-bollok"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "Sisters, cars and family milestones",
        "recap": "Mike took Aspen and Aniko to look at cars while Aniko’s Sweet Sixteen brought the family together. Aspen’s chapter belongs to the sisters’ family life amid the adults’ relationship decisions.",
        "status": "aired",
        "partnerPersonIds": [
          "p-aniko-bollok",
          "p-mike-silva",
          "p-darcey-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Questions for Georgi and a Tell All perspective",
        "recap": "Aspen and Aniko asked Georgi about his intentions toward Darcey during the Miami chapter. At the first Tell All, the sisters discussed social media attention and their place within a very public family story.",
        "status": "aired",
        "partnerPersonIds": [
          "p-aniko-bollok",
          "p-georgi-rusev",
          "p-darcey-silva"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 4,
        "title": "A sister’s college departure",
        "recap": "Aniko’s move to college in Arizona changed the family’s routine during the wedding-era season. Aspen remains part of the credited family cast; no separate dating, school or employment story is assigned to her.",
        "status": "aired",
        "partnerPersonIds": [
          "p-aniko-bollok",
          "p-darcey-silva",
          "p-stacey-silva"
        ]
      }
    ]
  },
  "p-michael-benz": {
    "origin": {
      "partnerName": "Stacey Silva",
      "partnerPersonId": "p-stacey-silva",
      "introLabel": "Introduced as the twins’ friend and fashion collaborator",
      "show": "Darcey & Stacey",
      "season": 3,
      "connectionKind": "friend"
    },
    "connections": [
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "His longtime friend and House of Eleven collaborator from the twins’ Los Angeles fashion beginnings."
      },
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "His longtime friend and fellow collaborator as House of Eleven’s Miami plans grew."
      },
      {
        "name": "Florian Sukaj",
        "personId": "p-florian-sukaj",
        "context": "Stacey’s husband, whose jealousy and disagreements with Michael became part of the series."
      },
      {
        "name": "Georgi Rusev",
        "personId": "p-georgi-rusev",
        "context": "Darcey’s partner and later wedding-party guest, with whom Michael clashed during season 4."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Old fashion friends, new Miami plans",
        "recap": "The twins introduced Michael as a longtime friend who had helped their early Los Angeles fashion work. Miami Swim Week brought the collaboration back into the story, while his views about their partners helped create tension with Florian.",
        "status": "aired",
        "partnerPersonIds": [
          "p-stacey-silva",
          "p-darcey-silva",
          "p-florian-sukaj"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 4,
        "title": "The launch and the wedding tensions",
        "recap": "Michael remained part of the twins’ business and personal circle during the fashion launch and wedding preparations. His confrontation with Georgi at the bachelorette events gave the supporting friends a particularly noisy chapter.",
        "status": "aired",
        "partnerPersonIds": [
          "p-stacey-silva",
          "p-darcey-silva",
          "p-georgi-rusev"
        ]
      }
    ]
  },
  "p-debbie-darcey-stacey": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "Introduced as the twins’ longtime friend",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "friend"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "Her longtime friend, whose relationship with Georgi supplied several group conversations."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "Her longtime friend, seen with Debbie during the first-season questions for Florian."
      },
      {
        "name": "Reina",
        "personId": "p-reina-darcey-stacey",
        "context": "Her fellow friend in the twins’ circle, appearing alongside her at dinners and their party."
      },
      {
        "name": "Florian Sukaj",
        "personId": "p-florian-sukaj",
        "context": "Stacey’s partner, whom the friends confronted about photographs in a licensed first-season preview."
      },
      {
        "name": "Georgi Rusev",
        "personId": "p-georgi-rusev",
        "context": "Darcey’s partner, whose introduction and disputed secondhand comments drew the friends into the story."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "The friends have a few questions",
        "recap": "Debbie and Reina questioned Florian about photographs with another woman, then met Georgi during the first season’s dinners. Debbie’s route into the story was her friendship with the twins.",
        "status": "aired",
        "partnerPersonIds": [
          "p-stacey-silva",
          "p-darcey-silva",
          "p-reina-darcey-stacey",
          "p-florian-sukaj",
          "p-georgi-rusev"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "Secondhand claims, firsthand tension",
        "recap": "Debbie relayed comments attributed to Georgi, contributing to Darcey’s distrust and a difficult exchange with her friends. The claims remain disputed parts of the televised disagreement, not independently verified conduct.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-reina-darcey-stacey",
          "p-georgi-rusev"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "A party before Miami",
        "recap": "Licensed episode notes name Debbie and Reina’s party ahead of the twins’ Miami trip. The friends remained in Darcey’s circle while she and Georgi tried to rebuild trust and the family prepared for the fashion show.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva",
          "p-reina-darcey-stacey",
          "p-georgi-rusev"
        ]
      }
    ]
  },
  "p-reina-darcey-stacey": {
    "origin": {
      "partnerName": "Darcey Silva",
      "partnerPersonId": "p-darcey-silva",
      "introLabel": "Introduced as the twins’ longtime friend",
      "show": "Darcey & Stacey",
      "season": 1,
      "connectionKind": "friend"
    },
    "connections": [
      {
        "name": "Darcey Silva",
        "personId": "p-darcey-silva",
        "context": "Her longtime friend, to whom Reina gave her impression of Georgi after their dinner."
      },
      {
        "name": "Stacey Silva",
        "personId": "p-stacey-silva",
        "context": "Her longtime friend, whose relationship with Florian brought the circle together."
      },
      {
        "name": "Debbie",
        "personId": "p-debbie-darcey-stacey",
        "context": "Her fellow friend and party host in the twins’ supporting circle."
      },
      {
        "name": "Florian Sukaj",
        "personId": "p-florian-sukaj",
        "context": "Stacey’s partner, questioned by the friends in the licensed first-season dinner preview."
      },
      {
        "name": "Georgi Rusev",
        "personId": "p-georgi-rusev",
        "context": "Darcey’s partner, whose previous marriage and later trust disputes were discussed with the friends."
      }
    ],
    "seasons": [
      {
        "show": "Darcey & Stacey",
        "season": 1,
        "title": "Meeting the partners at dinner",
        "recap": "Reina joined Debbie in questions about Florian’s photographs and at the dinner introducing Georgi. She asked about Georgi’s previous marriage and shared her views with Darcey.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva",
          "p-debbie-darcey-stacey",
          "p-florian-sukaj",
          "p-georgi-rusev"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 2,
        "title": "Gossip complicates the engagement",
        "recap": "Reina participated in the friends’ disagreement with Darcey over claims about Georgi. The recap treats that story as secondhand, disputed gossip rather than a certified account of what Georgi said.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-debbie-darcey-stacey",
          "p-georgi-rusev"
        ]
      },
      {
        "show": "Darcey & Stacey",
        "season": 3,
        "title": "Friends gather before the fashion trip",
        "recap": "Reina and Debbie’s party brought the friends and Silva family together before Miami Swim Week. It kept Reina within Darcey’s supporting circle as the relationship and business stories overlapped.",
        "status": "aired",
        "partnerPersonIds": [
          "p-darcey-silva",
          "p-stacey-silva",
          "p-debbie-darcey-stacey"
        ]
      }
    ]
  }
}

export const darceyStaceySeasonAdditions: Record<string, CastSeasonChapter[]> = {
  "p-darcey-silva": [
    {
      "show": "Darcey & Stacey",
      "season": 1,
      "title": "A new romance, a bigger family story",
      "recap": "Darcey began dating Georgi after their Miami meeting, and quarantine accelerated their relationship. Introducing him to the family brought questions about his previous marriage before a Rhode Island proposal.",
      "status": "aired",
      "partnerPersonIds": [
        "p-georgi-rusev"
      ],
      "relationshipId": "ARC-DS-DARCEY-GEORGI"
    },
    {
      "show": "Darcey & Stacey",
      "season": 2,
      "title": "Trust, finances and a trip to Turkey",
      "recap": "Money disagreements and a meeting with Georgi’s ex-wife strained the engagement. Georgi’s conversation with Jesse complicated matters while Darcey and Stacey headed to Turkey for a planned transformation.",
      "status": "aired",
      "partnerPersonIds": [
        "p-georgi-rusev",
        "p-stacey-silva"
      ],
      "relationshipId": "ARC-DS-DARCEY-GEORGI"
    },
    {
      "show": "Darcey & Stacey",
      "season": 3,
      "title": "Counseling, a pageant and wedding plans",
      "recap": "Darcey tried counseling with Georgi while supporting Aniko’s pageant and the twins’ Miami fashion show. A renewed engagement and proposed twin wedding led into the first two-part Tell All, where trust and the couple’s future returned to the agenda.",
      "status": "aired",
      "partnerPersonIds": [
        "p-georgi-rusev",
        "p-aniko-bollok",
        "p-stacey-silva"
      ],
      "relationshipId": "ARC-DS-DARCEY-GEORGI"
    },
    {
      "show": "Darcey & Stacey",
      "season": 4,
      "title": "Dating again while the family moves forward",
      "recap": "Darcey tried matchmaking and new dates in Miami while Aniko left for college. Georgi’s return through Florian’s wedding plans reopened unfinished feelings during Stacey’s preparations.",
      "status": "aired",
      "partnerPersonIds": [
        "p-georgi-rusev",
        "p-aniko-bollok",
        "p-stacey-silva"
      ],
      "relationshipId": "ARC-DS-DARCEY-GEORGI"
    }
  ],
  "p-stacey-silva": [
    {
      "show": "Darcey & Stacey",
      "season": 1,
      "title": "Florian’s arrival and a quarantine wedding",
      "recap": "Florian arrived from Albania on a K-1 visa, starting their 90-day countdown. Questions about photographs with another woman complicated the plans, but Stacey and Florian exchanged vows during quarantine.",
      "status": "aired",
      "partnerPersonIds": [
        "p-florian-sukaj"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    },
    {
      "show": "Darcey & Stacey",
      "season": 2,
      "title": "Family plans and difficult information",
      "recap": "Stacey and Florian explored having a child together, bringing fertility questions and different views on IVF into the conversation. The twins’ Turkey trip ran alongside that next-chapter decision.",
      "status": "aired",
      "partnerPersonIds": [
        "p-florian-sukaj",
        "p-darcey-silva"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    },
    {
      "show": "Darcey & Stacey",
      "season": 3,
      "title": "Miami fashion plans and a bigger wedding",
      "recap": "Stacey helped take House of Eleven to Miami Swim Week while planning a larger wedding with Florian. Jealousy, money and the choice between a move and a wedding complicated the celebrations before the first Tell All.",
      "status": "aired",
      "partnerPersonIds": [
        "p-florian-sukaj",
        "p-darcey-silva",
        "p-michael-benz"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    },
    {
      "show": "Darcey & Stacey",
      "season": 4,
      "title": "A wedding with more than a guest list to manage",
      "recap": "Stacey prepared for the wedding celebration while money concerns and disagreements with Darcey tested the plans. Her father’s health interrupted preparations before Stacey and Florian reached their ceremony.",
      "status": "aired",
      "partnerPersonIds": [
        "p-florian-sukaj",
        "p-darcey-silva",
        "p-mike-silva"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    }
  ],
  "p-florian-sukaj": [
    {
      "show": "Darcey & Stacey",
      "season": 1,
      "title": "Ninety days, photographs and vows",
      "recap": "Florian arrived in America to marry Stacey, beginning their K-1 countdown. Their private quarantine wedding did not immediately end the family’s questions about photographs with another woman.",
      "status": "aired",
      "partnerPersonIds": [
        "p-stacey-silva"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    },
    {
      "show": "Darcey & Stacey",
      "season": 2,
      "title": "Talking about a family of their own",
      "recap": "Fertility appointments and the possibility of IVF brought Florian’s beliefs and Stacey’s hopes into the same discussion. The couple faced difficult information while deciding what their next chapter could look like.",
      "status": "aired",
      "partnerPersonIds": [
        "p-stacey-silva"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    },
    {
      "show": "Darcey & Stacey",
      "season": 3,
      "title": "Jealousy meets Miami wedding plans",
      "recap": "The Miami fashion-show trip brought tension with the twins’ longtime collaborator Michael Benz. Florian and Stacey also weighed the cost of a Miami move against plans for a larger wedding, then joined the first Tell All.",
      "status": "aired",
      "partnerPersonIds": [
        "p-stacey-silva",
        "p-michael-benz"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    },
    {
      "show": "Darcey & Stacey",
      "season": 4,
      "title": "Work, a wedding party and a family scare",
      "recap": "Wedding finances and Florian’s work plans tested the couple while his choice of Georgi for the wedding party stirred tension. The celebration continued through family disagreements and concern for Mike’s health.",
      "status": "aired",
      "partnerPersonIds": [
        "p-stacey-silva",
        "p-georgi-rusev",
        "p-mike-silva"
      ],
      "relationshipId": "ARC-9D1935CA31D5"
    }
  ],
  "p-jesse-meester": [
    {
      "show": "Darcey & Stacey",
      "season": 2,
      "title": "An ex joins Georgi’s conversation",
      "recap": "A licensed preview shows Georgi speaking virtually with Jesse about Darcey’s past. His return supplied guest history during Darcey and Georgi’s trust dispute; it did not begin a new Jesse–Darcey courtship.",
      "status": "aired",
      "partnerPersonIds": [
        "p-darcey-silva"
      ]
    },
    {
      "show": "Darcey & Stacey",
      "season": 3,
      "title": "Old relationships at the Tell All",
      "recap": "The second Tell All part brought Jesse and Tom together as Darcey’s past relationships reentered the discussion. This is a returning-guest chapter, separate from Jesse’s original Before the 90 Days story.",
      "status": "aired",
      "partnerPersonIds": [
        "p-darcey-silva",
        "p-tom-brooks"
      ]
    }
  ],
  "p-tom-brooks": [
    {
      "show": "Darcey & Stacey",
      "season": 1,
      "title": "A meeting with Stacey, not a new romance",
      "recap": "The first-season episode notes identify Stacey’s meeting with Tom as Darcey moved toward a new relationship. His involvement reflects the history of the previous couple rather than a new romantic origin.",
      "status": "aired",
      "partnerPersonIds": [
        "p-stacey-silva",
        "p-darcey-silva"
      ]
    },
    {
      "show": "Darcey & Stacey",
      "season": 3,
      "title": "A returning ex at the Tell All",
      "recap": "Tom and Jesse met during the second part of the first Darcey & Stacey Tell All. Their guest appearances revisited Darcey’s relationship history without creating a new Tom–Darcey season-long dating arc.",
      "status": "aired",
      "partnerPersonIds": [
        "p-darcey-silva",
        "p-jesse-meester"
      ]
    }
  ]
}

